// ---------------------------------------------------------------------------
// Arbeitstage statt Kalendertage — CLAUDE.md Prinzip 16 (Daniel 2026-07-30)
// ---------------------------------------------------------------------------
// «alle 24h ausser an wochenenden (keine Notifications an Wochenenden!!!)».
// Wenn das OS am Samstag und Sonntag schweigt, darf es die beiden Tage auch
// nicht mitzaehlen: wer am Freitag nichts tut und am Montag mit «seit 3 Tagen»
// begruesst wird, wird fuer zwei Tage gemahnt, an denen das OS selbst nichts
// gesagt hat.
//
// GELTUNGSBEREICH (bewusst eng): die Zaehler, die einen MENSCHEN wegen
// Untaetigkeit anstupsen — heute die Lead-Leiter (Zuweisung, Erstkontakt,
// Richtpreis) und ihre Eskalationsstufen. NICHT die Alterungs-Fristen der
// Lebenszyklus-Karte (HOME-12, 30/60 Tage) und nicht die Fach-Fristen
// (MwSt-Termin, Rechnungs-Faelligkeit) — die haengen an echten Kalenderdaten,
// nicht an der Frage «wie lange schaut da schon niemand hin».
//
// Zeitzone: die Wochenend-Grenze ist die Schweizer, nicht die von UTC. Ein
// Freitagabend-Lead darf am Samstag 01:00 MEZ nicht schon als «Samstag
// vorbei» zaehlen.

const DAY_MS = 24 * 60 * 60 * 1000;

// Intl-Formatter ist teuer zu bauen, aber beliebig oft wiederverwendbar.
let weekdayFormatter: Intl.DateTimeFormat | null = null;

const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Wochentag in Europe/Zurich: 0 = Sonntag … 6 = Samstag. */
export function zurichWeekday(date: Date): number {
  weekdayFormatter ??= new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Zurich",
    weekday: "short",
  });
  return WEEKDAY_INDEX[weekdayFormatter.format(date)] ?? date.getUTCDay();
}

/** Faellt der Zeitpunkt auf einen Schweizer Samstag oder Sonntag? */
export function isWeekendInZurich(date: Date): boolean {
  const w = zurichWeekday(date);
  return w === 0 || w === 6;
}

/**
 * Ganze ARBEITSTAGE zwischen `from` und `now` — dieselbe Grundrechnung wie
 * `Math.floor((now - from) / 24h)`, nur ohne die Samstage und Sonntage, die
 * dazwischen lagen.
 *
 * Ungueltiges oder zukuenftiges `from` ergibt 0 (wie die Kalendertag-Variante
 * vorher: defensiv gegen Uhren-Drift).
 *
 * Rechenweg: der i-te vergangene Tag endet bei `from + i * 24h`; sein
 * Schweizer Wochentag ist `(Wochentag(from) + i) mod 7`. Damit braucht es
 * hoechstens sechs Schleifendurchlaeufe statt einer Schleife ueber alle Tage —
 * ein 71 Tage alter Lead kostet dasselbe wie ein zwei Tage alter.
 */
export function workdaysBetween(from: Date, now: Date): number {
  const start = from.getTime();
  const end = now.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  const total = Math.floor((end - start) / DAY_MS);
  if (total <= 0) return 0;

  const anchor = zurichWeekday(from);
  // Jede volle Woche enthaelt genau zwei Wochenendtage.
  const fullWeeks = Math.floor(total / 7);
  let weekendDays = fullWeeks * 2;
  for (let i = fullWeeks * 7 + 1; i <= total; i++) {
    const w = (anchor + i) % 7;
    if (w === 0 || w === 6) weekendDays++;
  }
  return Math.max(0, total - weekendDays);
}

/**
 * Die Gegenrichtung: WANN ist `days` Arbeitstage nach `from` erreicht?
 *
 * `workdaysBetween` beantwortet «wie viele Arbeitstage sind seither vergangen»,
 * diese Funktion «ab wann sind es genug». Beide Fragen muessen dieselbe Antwort
 * geben, darum RECHNET hier nichts selbst: die Schleife fragt Kalendertag um
 * Kalendertag `workdaysBetween` und nimmt den ersten, der reicht. Eine zweite
 * Wochenend-Arithmetik neben der oberen waere genau die Doppel-Ableitung, die
 * in diesem Haus schon mehrfach auseinandergelaufen ist.
 *
 * Beispiel (LEAD-13, ein Arbeitstag): ein Lead vom Freitag 14:00 ist am Montag
 * 14:00 faellig, nicht am Samstag — dazwischen liegen zwei Tage, an denen das
 * OS selbst schweigt.
 *
 * Die Schleife ist kurz und endlich: zwischen `days` Arbeitstagen liegen
 * hoechstens zwei Wochenendtage je angefangener Woche.
 */
export function workdayDeadline(from: Date, days: number): Date {
  const start = from.getTime();
  if (!Number.isFinite(start) || days <= 0) return new Date(start);
  const maxKalendertage = days + 2 * (Math.ceil(days / 5) + 1);
  for (let total = days; total <= maxKalendertage; total++) {
    const at = new Date(start + total * DAY_MS);
    if (workdaysBetween(from, at) >= days) return at;
  }
  /* c8 ignore next 2 -- unerreichbar: maxKalendertage deckt jedes Wochenende ab */
  return new Date(start + maxKalendertage * DAY_MS);
}

/** Wie `workdaysBetween`, aber mit ISO-String als Startpunkt. */
export function workdaysSince(iso: string, now: Date): number {
  const from = new Date(iso);
  if (Number.isNaN(from.getTime())) return 0;
  return workdaysBetween(from, now);
}

/**
 * «heute» / «gestern» / «vor N Arbeitstagen» — die Begruendungszeile einer
 * Meldung, die jemanden an offene Arbeit erinnert.
 *
 * «gestern» heisst hier «einen Arbeitstag her»: am Montag ist der Freitag
 * gestern. Das ist genau die Lesart, die Prinzip 16 verlangt — das Wochenende
 * hat nicht stattgefunden.
 */
export function workdaysAgoLabel(iso: string, now: Date): string {
  const days = workdaysSince(iso, now);
  if (days === 0) return "heute";
  if (days === 1) return "gestern";
  return `vor ${String(days)} Arbeitstagen`;
}

// ---------------------------------------------------------------------------
// Die Stunde in Zuerich
// ---------------------------------------------------------------------------
// Bis AUTO-2 hing hier das Sendefenster des Richtpreis-Automaten (Werktag
// 07–19 Uhr, AUTO-1). Daniel-Entscheid 23.09.2026: der Richtpreis geht rund
// um die Uhr raus, am Wochenende ohne Kopie ans Team (`roofAutoSendCc` in
// `shared/roof/dispatch.ts`). Geblieben ist die Stunde — der Montags-Nachtrag
// geht ab 07:00 Zuerich raus (`shared/roof/weekendNotice.ts`).

let hourFormatter: Intl.DateTimeFormat | null = null;

/** Volle Stunde in Europe/Zurich, 0 … 23. */
export function zurichHour(date: Date): number {
  hourFormatter ??= new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Zurich",
    hour: "numeric",
    hourCycle: "h23",
  });
  const parsed = Number.parseInt(hourFormatter.format(date), 10);
  return Number.isFinite(parsed) ? parsed : date.getUTCHours();
}

// ---------------------------------------------------------------------------
// NACHT-1 — das Zustellfenster fuer Pushes (Plan-Chat-Entscheid 07.10.2026)
// ---------------------------------------------------------------------------
// Zugestellt wird Montag bis Freitag von 07:00 bis 19:00 Schweizer Zeit.
// Ausserhalb bleibt das Geraet still; was in der Nacht entsteht, kommt am
// naechsten Werktagmorgen als EINE Sammel-Nachricht.

export const PUSH_FENSTER_VON_STUNDE = 7;
export const PUSH_FENSTER_BIS_STUNDE = 19;

/** Liegt der Zeitpunkt im Zustellfenster (Mo–Fr 07:00–18:59 Schweizer Zeit)? */
export function isPushFensterInZurich(date: Date): boolean {
  if (isWeekendInZurich(date)) return false;
  const h = zurichHour(date);
  return h >= PUSH_FENSTER_VON_STUNDE && h < PUSH_FENSTER_BIS_STUNDE;
}

/** Werktag, aber ausserhalb des Fensters — die Nachtruhe (nicht das Wochenende). */
export function isNachtruheInZurich(date: Date): boolean {
  return !isWeekendInZurich(date) && !isPushFensterInZurich(date);
}
